import { Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import moment from 'moment';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as LinkIcon } from '../../../../../assets/icons/expand-link.svg';
import { ReactComponent as PDFIcon } from '../../../../../assets/icons/file.svg';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { FileStructureContext } from '../../../../../contexts/FileStructureContext';
import { updateFileFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { CompanyFile, Dashboard } from '../../../../../types/files';
import DeckListModal from '../../../../modals/tables/dashboards/rows/DeckListModal';
import CompanyMiniDeckViewer from '../../../../molecules/dashboard-deck/CompanyMiniDeckViewer';
import useStyles from "../styles";

export const useLocalStyles = makeStyles((theme) => ({
    pdfWrapper: {
        padding: '2px 16px',
        backgroundColor: theme.colors.error['50'],
        border: `1px solid ${theme.colors.error['50']}`,
        borderRadius: 8,
        cursor: 'pointer',
    },
    deckLinkWrapper: {
        padding: '4px 16px',
        backgroundColor: theme.colors.blue['50'],
        border: `1px solid #eff6ff`,
        borderRadius: 8,
    },
}));

const DeckTeaserCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const localClasses = useLocalStyles();
    const { userGroup } = useContext(AuthContext);
    const { fileStructure } = useContext(FileStructureContext);

    const [deckViewerModalOpen, setDeckViewerModalOpen] = useState<boolean>(false);
    const [deckListModalOpen, setDeckListModalOpen] = useState<boolean>(false);
    const deckRef = useRef<HTMLElement>(null);

    const sanitizeUrl = (originUrl: string) => originUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');

    const files = useMemo(() => fileStructure
        .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
        .sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboard, fileStructure]);

    const defaultFile = useMemo(() => files.find(file => file.isDeck) ?? files[0], [files]);

    const handleSetDeck = useCallback(async (file: CompanyFile) => {
        const deckFiles = files?.filter(f => f.isDeck);
        const promises: Promise<CompanyFile>[] = [];

        deckFiles?.forEach(dF => {
            promises.push(updateFileFunc({
                id: dF.id,
                group: userGroup,
                documentCategory: dF.documentCategory,
                status: dF.status,
                dashboardId: dF.dashboardId,
                marketMetrics: dF.marketMetrics,
                isDeck: false,
            }))
        });

        promises.push(updateFileFunc({
            id: file.id,
            group: userGroup,
            documentCategory: file.documentCategory,
            status: file.status,
            dashboardId: file.dashboardId,
            marketMetrics: file.marketMetrics,
            isDeck: true,
        }));

        await Promise.all(promises);
    }, [files, userGroup]);

    useEffect(() => {
        if (!!files[0] && !files[0]?.isDeck)
            handleSetDeck(files[0]);
        //eslint-disable-next-line
    }, [files]);

    return (<>
        <Stack width="100%" ref={deckRef}>
            {(!!defaultFile) ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    <Typography
                        className={classNames(classes.url, localClasses.pdfWrapper)}
                        fontWeight={`bold !important`}
                        color={`${theme.colors.error['700']} !important`}
                        onClick={() => setDeckViewerModalOpen(true)}>
                        <PDFIcon style={{ position: 'relative', bottom: -3, right: 6, width: 20, height: 18, stroke: theme.colors.error['700'] }} />
                        {defaultFile.name}
                    </Typography>
                </Stack>
            ): !!dashboard.externalLinks?.length ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    <Typography
                        component="a"
                        className={classNames(classes.url, localClasses.deckLinkWrapper)}
                        fontWeight={`bold !important`}
                        color={`#1d4ed8 !important`}
                        href={dashboard?.externalLinks?.at(-1) || ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ cursor: 'pointer !important' }}>
                        <LinkIcon style={{ position: 'relative', bottom: -3, right: 6, width: 20, height: 18, stroke: '#1d4ed8' }} />
                        {sanitizeUrl(dashboard?.externalLinks?.at(-1) || '')}
                    </Typography>
                </Stack>
            ) : (<Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>{'No deck/teaser'}</Typography>)}
        </Stack>
        {deckViewerModalOpen && (
            <CompanyMiniDeckViewer
                open={deckViewerModalOpen}
                defaultDeck={defaultFile}
                externalDeck={dashboard?.externalLinks?.at(-1)}
                onClose={() => setDeckViewerModalOpen(false) } />
        )}
        {deckListModalOpen && (
            <DeckListModal open={deckListModalOpen}
                dashboard={dashboard}
                files={files}
                anchorElement={deckRef.current}
                onSetDeck={handleSetDeck}
                onClose={() => setDeckListModalOpen(false)} />
        )}
    </>);
}

export default memo(DeckTeaserCell);
